/**
 * Header search (snippets/predictive-search.liquid).
 *
 * The markup for results comes from the store, not from this file:
 *   - live results: sections/predictive-search.liquid via /search/suggest
 *   - best sellers: sections/predictive-search-bestsellers.liquid, fetched once
 * so prices, images and links are rendered by Liquid. This script only fetches,
 * swaps panels and handles the keyboard.
 *
 * Zipchat's AI search takes over this input while it is switched on (see the
 * snippet). When Zipchat is present, a "Pitajte asistenta" line hands the
 * typed words to its chat.
 */
const MIN_CHARS = 2;
const DEBOUNCE_MS = 180;

const SUGGEST_URL = (q) => {
  const p = new URLSearchParams({
    q,
    'resources[type]': 'collection,product',
    'resources[limit]': '8',
    'resources[limit_scope]': 'each',
    'resources[options][unavailable_products]': 'last',
    'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
    section_id: 'predictive-search',
  });
  return `${window.Shopify?.routes?.root || '/'}search/suggest?${p}`;
};

const BESTSELLERS_URL =
  `${window.Shopify?.routes?.root || '/'}collections/all?sort_by=best-selling&filter.v.availability=1&section_id=predictive-search-bestsellers`;

const escapeHtml = (s) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const FOLD = { š: 's', č: 'c', ć: 'c', ž: 'z', đ: 'd' };
const fold = (s) => s.toLowerCase().replace(/[ščćžđ]/g, (c) => FOLD[c]);

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.openBtn = this.querySelector('[data-open-search]');
    this.panel = this.querySelector('[data-ps-panel]');
    this.form = this.querySelector('[data-ps-form]');
    this.input = this.querySelector('[data-search-input]');
    this.clearBtn = this.querySelector('[data-ps-clear]');
    this.body = this.querySelector('[data-ps-body]');
    this.results = this.querySelector('[data-ps-results]');
    this.idle = this.querySelector('[data-ps-idle]');
    this.noResults = this.querySelector('[data-ps-noresults]');
    this.bestWrap = this.querySelector('[data-ps-best-wrap]');
    this.best = this.querySelector('[data-ps-best]');
    this.status = this.querySelector('[data-ps-status]');

    this.cache = new Map();
    this.controller = null;
    this.timer = null;
    this.bestLoaded = false;

    this.openBtn?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.querySelectorAll('[data-close-search]').forEach((b) => b.addEventListener('click', () => this.close(true)));

    this.input?.addEventListener('input', () => this.onInput());
    this.input?.addEventListener('focus', () => { if (!this.isOpen) this.open(); });
    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });

    this.addEventListener('keydown', (e) => this.onKeydown(e));

    this.form?.addEventListener('submit', (e) => {
      if (this.input.value.trim().length === 0) e.preventDefault();
    });

    // Hand the typed words to Zipchat's chat, when the app is on the page.
    this.addEventListener('click', (e) => {
      const ask = e.target.closest('[data-ps-ask]');
      if (!ask) return;
      e.preventDefault();
      const q = this.input.value.trim();
      this.close();
      window.Zipchat?.open?.({ searchQuery: q, source: 'theme_search' });
    });

    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.contains(e.target)) this.close();
    });
  }

  get isOpen() {
    return this.panel?.classList.contains('is-active');
  }

  open() {
    // Zipchat's AI search has taken over this input (it flags the element it
    // hijacks). Hand the click to its capture listener instead of opening this
    // panel behind its overlay; the panel's input is hidden, so focusing it
    // would not reach Zipchat either.
    if (this.input?._zipchatSearchHijacked) {
      this.input.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return;
    }
    // Zipchat closes "native" search UIs by calling close() on any ancestor of
    // its input that has an [open] attribute, so expose that state too.
    this.setAttribute('open', '');
    this.panel.classList.add('is-active');
    this.openBtn?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ps-open');
    this.loadBestsellers();
    // The panel fades in; focus once it accepts pointer events.
    setTimeout(() => this.input?.focus(), 50);
  }

  close(returnFocus = false) {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.panel.classList.remove('is-active');
    this.openBtn?.setAttribute('aria-expanded', 'false');
    this.input?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ps-open');
    this.controller?.abort();
    if (returnFocus) this.openBtn?.focus();
  }

  onInput() {
    const q = this.input.value.trim();
    this.clearBtn.hidden = this.input.value.length === 0;
    clearTimeout(this.timer);
    if (q.length < MIN_CHARS) {
      this.controller?.abort();
      this.setLoading(false);
      this.showIdle();
      return;
    }
    this.timer = setTimeout(() => this.search(q), DEBOUNCE_MS);
  }

  async search(q) {
    if (this.cache.has(q)) {
      this.render(q, this.cache.get(q));
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    this.setLoading(true);
    try {
      const res = await fetch(SUGGEST_URL(q), { signal: this.controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const response = doc.querySelector('[data-ps-response]');
      const data = {
        count: Number(response?.dataset.psCount || 0),
        html: response ? response.innerHTML : '',
      };
      this.cache.set(q, data);
      // A slower, older request must not overwrite what the shopper typed since.
      if (this.input.value.trim() === q) this.render(q, data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Predictive search:', err);
      this.showIdle(`Pretraga trenutno ne radi. Pritisnite Enter za sve rezultate za „${escapeHtml(q)}“.`);
    } finally {
      this.setLoading(false);
    }
  }

  render(q, data) {
    if (data.count === 0) {
      this.showIdle(
        `Nismo našli ništa za „${escapeHtml(q)}“. Proverite reč ili pogledajte ispod.${this.askHtml(q)}`,
      );
      this.announce('Nema rezultata');
      return;
    }
    this.results.innerHTML = data.html;
    const foot = this.results.querySelector('.ps-foot');
    if (foot && this.canAsk) foot.insertAdjacentHTML('beforeend', this.askHtml(q));
    this.highlight(q);
    this.results.hidden = false;
    this.idle.hidden = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.body.scrollTop = 0;
    const n = this.results.querySelectorAll('.ps-card').length;
    this.announce(n === 1 ? '1 proizvod' : `${n} proizvoda`);
  }

  showIdle(message = '') {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.idle.hidden = false;
    this.noResults.hidden = !message;
    this.noResults.innerHTML = message;
    this.input.setAttribute('aria-expanded', message ? 'true' : 'false');
  }

  get canAsk() {
    return typeof window.Zipchat?.open === 'function';
  }

  askHtml(q) {
    if (!this.canAsk) return '';
    return `<button type="button" class="ps-ask" data-ps-ask data-ps-item>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8 10h8M8 14h5m-9 6 3.5-3H19a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h1z"/></svg>
      <span>Pitajte našeg asistenta: „${escapeHtml(q)}“</span>
    </button>`;
  }

  async loadBestsellers() {
    if (this.bestLoaded) return;
    this.bestLoaded = true;
    try {
      const res = await fetch(BESTSELLERS_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = doc.querySelector('[data-ps-bestsellers]');
      if (!list || list.children.length === 0) return;
      this.best.innerHTML = list.innerHTML;
      this.bestWrap.hidden = false;
    } catch (err) {
      // Categories still show; best sellers are a bonus.
      this.bestLoaded = false;
      console.error('Predictive search best sellers:', err);
    }
  }

  // Mark the typed words in product titles, also when typed without
  // diacritics ("kosulja" marks "košulja"). The fold is one character to one,
  // so match positions in the folded title are positions in the real one.
  // Built from text, so a title can never inject markup.
  highlight(q) {
    const terms = fold(q).split(/\s+/).filter((t) => t.length >= MIN_CHARS).map(escapeRegExp);
    if (terms.length === 0) return;
    const re = new RegExp(terms.join('|'), 'g');
    this.results.querySelectorAll('[data-ps-title]').forEach((el) => {
      const text = el.textContent;
      let html = '';
      let last = 0;
      for (const m of fold(text).matchAll(re)) {
        if (m[0].length === 0) continue;
        html += escapeHtml(text.slice(last, m.index)) + `<mark>${escapeHtml(text.slice(m.index, m.index + m[0].length))}</mark>`;
        last = m.index + m[0].length;
      }
      if (last > 0) el.innerHTML = html + escapeHtml(text.slice(last));
    });
  }

  setLoading(on) {
    this.panel.classList.toggle('is-loading', on);
  }

  announce(text) {
    if (this.status) this.status.textContent = text;
  }

  // Arrow keys walk the visible links and chips; Escape closes.
  onKeydown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this.close(true);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...this.body.querySelectorAll('[data-ps-item]')].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      items[i < 0 ? 0 : Math.min(i + 1, items.length - 1)].focus();
    } else if (i <= 0) {
      this.input.focus();
    } else {
      items[i - 1].focus();
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

export { PredictiveSearch };
